import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Pagination,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  Event,
  BugReport,
  Error as ErrorIcon,
  Warning,
  Info,
} from '@mui/icons-material';
import styled from '@emotion/styled';
import axios from '../../helpers/request';
import moment from 'moment';

const LogsContainer = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const LogsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LogEntry = styled(Box)(({ theme, logtype }) => {
  const colors = {
    event: theme.palette.info.main,
    debug: theme.palette.grey[500],
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.light,
  };

  return {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1.5),
    borderLeft: `4px solid ${colors[logtype] || theme.palette.grey[300]}`,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0 4px 4px 0',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };
});

const LogIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
}));

const LogContent = styled(Box)(() => ({
  flex: 1,
}));

const LogMeta = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

const MetadataChip = styled(Chip)(({ theme, chipcolor }) => ({
  marginRight: theme.spacing(0.5),
  marginTop: theme.spacing(0.5),
  height: '22px',
  fontSize: '0.9rem',
  fontWeight: 400,
  color: '#fff',
  backgroundColor: chipcolor || theme.palette.grey[600],
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

// Color mapping for metadata badge types
const METADATA_COLORS = {
  source: '#6366f1',           // Indigo - identifies data source
  networkIssue: '#ef4444',     // Red - network problems
  issueReason: '#f59e0b',      // Amber - issue description
  endReason: '#8b5cf6',        // Purple - why call ended
  errorCode: '#dc2626',        // Dark red - error codes
  errorCodeUrl: '#0891b2',     // Cyan - link to error info
  stats: '#10b981',            // Green - statistics
  issues: '#f97316',           // Orange - general issues
  default: '#64748b',          // Slate - default
};

// Human-readable labels for metadata keys
const METADATA_LABELS = {
  source: 'Source',
  networkIssue: 'Network Issue',
  issueReason: 'Issue Details',
  endReason: 'End Reason',
  errorCode: 'Error Code',
  errorCodeUrl: 'Error Info',
  stats: 'Statistics',
  issues: 'Issues Detected',
  participantSid: 'Participant ID',
  participantId: 'Participant ID',
  identity: 'Identity',
};

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  alignItems: 'center',
}));

const getLogIcon = (logType) => {
  switch (logType) {
    case 'event':
      return <Event fontSize="small" color="info" />;
    case 'debug':
      return <BugReport fontSize="small" color="action" />;
    case 'error':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'warning':
      return <Warning fontSize="small" color="warning" />;
    case 'info':
      return <Info fontSize="small" color="info" />;
    default:
      return <Event fontSize="small" />;
  }
};

const RoomLogs = ({ meetingId, roomSid }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });

  const fetchLogs = async (page = 1) => {
    if (!meetingId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });

      if (filter !== 'all') {
        params.append('log_type', filter);
      }

      const response = await axios.get(
        `/internal/meeting/${meetingId}/logs?${params.toString()}`
      );

      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || pagination);
    } catch (err) {
      console.error('Error fetching room logs:', err);
      // Don't show error if the table doesn't exist yet (expected on first deploy)
      if (err.response?.status !== 500 || !err.response?.data?.error?.message?.includes('room_logs')) {
        setError('Failed to load room logs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
  }, [meetingId, filter]);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPagination({ ...pagination, page: 1 });
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
    fetchLogs(newPage);
  };

  const formatTimestamp = (timestamp) => {
    return moment(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const renderMetadata = (metadata) => {
    if (!metadata) return null;

    const parsedMeta = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;

    // Filter out internal fields that don't need to be shown
    const hiddenFields = ['participantSid', 'participantId', 'identity'];
    const entries = Object.entries(parsedMeta).filter(([key, value]) =>
      value && !hiddenFields.includes(key)
    );

    if (entries.length === 0) return null;

    // Format value for display
    const formatValue = (key, value) => {
      if (typeof value === 'object') {
        // For stats object, show a summary
        if (key === 'stats') {
          const parts = [];
          if (value.avgPacketLoss) parts.push(`Loss: ${value.avgPacketLoss.toFixed(1)}%`);
          if (value.avgRoundTripTime) parts.push(`RTT: ${Math.round(value.avgRoundTripTime)}ms`);
          if (value.avgJitter) parts.push(`Jitter: ${Math.round(value.avgJitter)}ms`);
          if (value.totalBitrate) parts.push(`Bitrate: ${Math.round(value.totalBitrate)}kbps`);
          return parts.length > 0 ? parts.join(', ') : JSON.stringify(value);
        }
        return JSON.stringify(value);
      }
      // Format source values to be more readable
      if (key === 'source') {
        switch (value) {
          case 'video_log_analyzer': return 'VLA Analytics';
          case 'realtime_diagnostics': return 'Real-time';
          default: return value;
        }
      }
      return value;
    };

    return (
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {entries.map(([key, value]) => {
          const label = METADATA_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
          const color = METADATA_COLORS[key] || METADATA_COLORS.default;
          const displayValue = formatValue(key, value);

          return (
            <MetadataChip
              key={key}
              label={`${label}: ${displayValue}`}
              size="small"
              chipcolor={color}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <LogsContainer>
      <LogsHeader onClick={() => setExpanded(!expanded)}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">Room Activity Logs</Typography>
          {!loading && (
            <Chip
              label={pagination.total}
              size="small"
              sx={{ ml: 1 }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              fetchLogs(pagination.page);
            }}
            disabled={loading}
          >
            <Refresh fontSize="small" />
          </IconButton>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </LogsHeader>

      <Collapse in={expanded}>
        <CardContent>
          <FilterContainer>
            <Typography variant="body2" color="textSecondary">
              Filter by type:
            </Typography>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size="medium"
              sx={{
                '& .MuiToggleButton-root': {
                  padding: '5px 32px',
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="event">Events</ToggleButton>
              <ToggleButton value="debug">Debug</ToggleButton>
              <ToggleButton value="error">Errors</ToggleButton>
              <ToggleButton value="warning">Warnings</ToggleButton>
            </ToggleButtonGroup>
          </FilterContainer>

          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && logs.length === 0 && !error && (
            <Alert severity="info">
              No logs found for this room. Logs will appear here as room events occur.
            </Alert>
          )}

          {!loading && logs.length > 0 && (
            <>
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {logs.map((log) => (
                  <LogEntry key={log.id} logtype={log.log_type}>
                    <LogIcon>{getLogIcon(log.log_type)}</LogIcon>
                    <LogContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography variant="body2" fontWeight="medium">
                          {log.message}
                        </Typography>
                        <Chip
                          label={log.event_type || log.log_type}
                          size="small"
                          sx={{ ml: 1, textTransform: 'capitalize' }}
                        />
                      </Box>
                      <LogMeta>
                        {formatTimestamp(log.created_at)}
                        {log.metadata && renderMetadata(log.metadata)}
                      </LogMeta>
                    </LogContent>
                  </LogEntry>
                ))}
              </Box>

              {pagination.pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    size="small"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Collapse>
    </LogsContainer>
  );
};

export default RoomLogs;
